import { useState } from 'react';
import { toastService } from '../services/toastService';
import type { WatchTopic } from '../types';
import { watchTopicsService } from '../services/watchTopicsService';
import { useAuth } from './useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { Logger } from '../utils/logger';
// V4-FRONT-ANY-USAGE 2026-05-28
import { extractApiErrorMessage } from '../utils/apiError';

export function useWatchTopics() {
  const [watchTopics, setWatchTopics] = useState<WatchTopic[]>([]);
  // V4-WATCH-KEYWORDS-LOADING 2026-06-22 — ids des topics dont les mots-clés sont en
  // cours de génération en arrière-plan (enrichKeywords). Exposé à l'UI pour afficher
  // un indicateur "génération en cours" sur la card concernée.
  const [enrichingTopicIds, setEnrichingTopicIds] = useState<Set<string>>(new Set());
  const { currentSpace } = useAuth();
  const { t } = useLanguage();

  const getTopicKeywordsText = (topic: WatchTopic): string => {
    return topic.keywords.join(', ');
  };

  /**
   * Génère les mots-clés en arrière-plan (LLM) puis patche le topic, sans bloquer
   * la création. Maj ES silencieuse (pas de toast — l'ajout a déjà toasté).
   * Évite le blocage UI de ~1–3s sur `Enregistrer` (génération keywords inline).
   */
  const enrichKeywords = async (id: string, prompt: string) => {
    setEnrichingTopicIds((prev) => new Set(prev).add(id));
    try {
      const res = await watchTopicsService.generateKeywords(prompt);
      const keywords = res.keywords || [];
      if (keywords.length === 0) return;
      const updated = await watchTopicsService.update(id, { keywords });
      if (updated) {
        setWatchTopics((prev) => prev.map((topic) => (topic.id === id ? updated : topic)));
      }
    } catch (error: unknown) {
      Logger.error('Error enriching watch topic keywords:', error);
      // Échec silencieux : le topic reste sans mots-clés, l'utilisateur peut les générer manuellement.
    } finally {
      setEnrichingTopicIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // V4-WATCH-ADD-OPTIMISTIC-ROLLBACK : add/delete mettent le state à jour de
  // façon optimiste (avant la confirmation serveur) pour un feedback UI instant.
  // On capture le snapshot de l'état précédent AVANT la mutation et on le
  // restaure dans le catch si l'API échoue — sinon l'UI reste désynchronisée
  // (topic fantôme affiché, ou topic supprimé à tort).
  const addWatchTopic = async (topicData: Omit<WatchTopic, 'id'>) => {
    if (!currentSpace) {
      toastService.error(t.toasts.noSpaceSelected);
      return;
    }
    const previousTopics = watchTopics;
    try {
      const data = await watchTopicsService.create(currentSpace.id, topicData);

      if (data) {
        setWatchTopics((prev) => [...prev, data]);
        toastService.success(
          t.toasts.topicAddedTitle,
          t.toasts.topicAddedDesc.replace('{keywords}', getTopicKeywordsText(data))
        );
        // Aucun mot-clé fourni → génération IA en arrière-plan (non bloquant).
        if ((!topicData.keywords || topicData.keywords.length === 0) && data.prompt) {
          void enrichKeywords(data.id, data.prompt);
        }
      }
    } catch (error: unknown) {
      // V4-FRONT-ANY-USAGE 2026-05-28
      Logger.error('Error adding watch topic:', error);
      // V4-WATCH-ADD-OPTIMISTIC-ROLLBACK : rollback vers l'état pré-ajout.
      setWatchTopics(previousTopics);
      toastService.error(extractApiErrorMessage(error, "Erreur lors de l'ajout du sujet"));
    }
  };

  const updateWatchTopic = async (id: string, topicData: Omit<WatchTopic, 'id'>) => {
    try {
      const data = await watchTopicsService.update(id, topicData);

      if (data) {
        setWatchTopics((prev) =>
          prev.map((topic) => (topic.id === id ? data : topic))
        );
        toastService.success(
          t.toasts.topicUpdatedTitle,
          t.toasts.topicUpdatedDesc.replace('{keywords}', getTopicKeywordsText(data))
        );
      }
    } catch (error: unknown) {
      // V4-FRONT-ANY-USAGE 2026-05-28
      Logger.error('Error updating watch topic:', error);
      toastService.error(extractApiErrorMessage(error, 'Erreur lors de la mise à jour du sujet'));
    }
  };

  const deleteWatchTopic = async (id: string) => {
    // V4-WATCH-ADD-OPTIMISTIC-ROLLBACK : suppression optimiste avec snapshot.
    const previousTopics = watchTopics;
    setWatchTopics((prev) => prev.filter((topic) => topic.id !== id));
    try {
      await watchTopicsService.delete(id);
      toastService.success(t.toasts.topicDeletedTitle, t.toasts.topicDeletedDesc);
    } catch (error: unknown) {
      // V4-FRONT-ANY-USAGE 2026-05-28
      Logger.error('Error deleting watch topic:', error);
      // V4-WATCH-ADD-OPTIMISTIC-ROLLBACK : rollback — le topic supprimé à tort
      // réapparaît (restaure l'état pré-suppression).
      setWatchTopics(previousTopics);
      toastService.error(extractApiErrorMessage(error, 'Erreur lors de la suppression du sujet'));
    }
  };

  return {
    watchTopics,
    setWatchTopics,
    enrichingTopicIds,
    addWatchTopic,
    updateWatchTopic,
    deleteWatchTopic
  };
}

